import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  HostBinding,
  HostListener,
  OnDestroy,
  OnInit,
  Optional,
  ViewContainerRef,
} from '@angular/core';
import { combineLatest, filter, forkJoin, interval, Subject, Subscription, takeUntil } from 'rxjs';

import { AddaDynamicLoaderService } from '@addatech/basic';
import { registerLocaleData } from '@angular/common';
import localeGerm from '@angular/common/locales/de';
import localeEnCA from '@angular/common/locales/en-CA';
import localeSpan from '@angular/common/locales/es-MX';
import localeFrCaExtra from '@angular/common/locales/extra/fr-CA';
import localeFr from '@angular/common/locales/fr';
import localeFrCA from '@angular/common/locales/fr-CA';
import { Router, RouterOutlet } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { select, Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UserIdleService } from 'angular-user-idle';
import moment from 'moment';
import { GraphService, SSOLoginStatus } from './auth/graph.service';
import { InitialStartupService } from './auth/initial-startup.service';
import { MsalConfig } from './auth/msal.config';
import { SsoLogoutService } from './auth/sso-logout.service';
import { HeaderComponent } from './header/header.component';
import { WindowSizeModel } from './shared/layout/services/window-size.model';
import { LocalStorageService } from './shared/local-storage.service';
import { ClinicApplicationSetupDto } from './shared/models/central-office/co-clinic-application-setup.model';
import { Identifier } from './shared/models/storage.models';
import { SharedPopupModule } from './shared/popup/popup.module';
import { ClinicHttpService } from './shared/services/clinic-http.service';
import { DevExtremeConfigService } from './shared/services/devextreme-config.service';
import { HelpService } from './shared/utils/helpers/help-tooltip/help.service';
import { State } from './store';
import { setUserIdle, setWindowSizeChanged } from './store/ui/ui.actions';
import { getContentLoadingState, getUserIdle } from './store/ui/ui.selectors';
import { selectAuthenticatedUser } from './store/user/user.selectors';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [HeaderComponent, RouterOutlet, SharedPopupModule, TranslateModule],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  @HostBinding('class') classes = '';
  title = 'Central Office Web';
  loadSub?: Subscription | null = null;
  private subscription: Subscription = new Subscription();

  userSecondsIdle?: number;
  idleTimeOutLimit = 300;
  displayIdleConfirmDialog = false;
  applicationSetup?: ClinicApplicationSetupDto;
  currClinicId = Number(localStorage.getItem(Identifier.TYPE_CLINIC_ID));
  moment = moment;

  progressValue = 0;
  showProgress = false;
  private timerSub?: Subscription;

  private idleSubscription: Subscription = new Subscription();
  private jwtHelper = new JwtHelperService();

  constructor(
    private translateService: TranslateService,
    private localStorageService: LocalStorageService,
    private graphService: GraphService,
    private dynamicLoaderService: AddaDynamicLoaderService,
    @Optional() private msalService: MsalService,
    @Optional() private msalBroadcastService: MsalBroadcastService,
    private helpService: HelpService,
    private ref: ChangeDetectorRef,
    private store: Store<State>,
    private userIdle: UserIdleService,
    private router: Router,
    private clinicHttpService: ClinicHttpService,
    private authService: InitialStartupService,
    private ssoLogoutService: SsoLogoutService,
    public viewContRef: ViewContainerRef,
    private dxConfigService: DevExtremeConfigService
  ) {
    this.dynamicLoaderService.viewContainerRef = this.viewContRef;
    this.dxConfigService.initialize();
    registerLocaleData(localeFr);
    registerLocaleData(localeFrCA, localeFrCaExtra);
    registerLocaleData(localeEnCA);
    registerLocaleData(localeSpan);
    registerLocaleData(localeGerm);

    translateService.addLangs(['en-CA', 'fr-CA']);
  }

  @HostListener('window:resize')
  onResize(): void {
    const windowSize: WindowSizeModel = {
      width: document.documentElement.offsetWidth,
      height: document.documentElement.offsetHeight,
    };
    this.store.dispatch(setWindowSizeChanged({ windowSize: windowSize }));
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    if (this.displayIdleConfirmDialog) {
      if (event.key === 'Escape') {
        this.restartIdleTimer();
      }
    }
  }

  private subscribeToLanguageLocalStorage(): void {
    this.subscription.add(
      this.localStorageService.getStorageItem(Identifier.TYPE_LANGUAGE).subscribe((lang) => this.setLanguage(lang))
    );
  }

  private setLanguage(lang: string | null): void {
    let curLang = 'en-CA';
    if (lang && lang !== '') {
      curLang = lang;
    }
    this.translateService.setDefaultLang(curLang);
    this.translateService.use(curLang);
    this.dxConfigService.setLocale(curLang);
    switch (curLang) {
      case 'fr-CA':
        moment.locale('fr');
        break;
      default:
        moment.locale('en');
        break;
    }
    this.ref.markForCheck();
  }

  ngOnInit() {
    this.subscribeToLanguageLocalStorage();
    this.subscribeToTokenLocalStorage();

    this.subscription.add(
      this.store.pipe(select(getContentLoadingState)).subscribe((isContentLoading) => {
        Promise.resolve().then(() => {
          if (isContentLoading) {
            this.startProgress();
          } else {
            this.finishProgress();
          }
        });
      })
    );

    if (MsalConfig.isEnabled) {
      this.msalService.handleRedirectObservable().subscribe();
      this.setLoginDisplay();
      this.msalService.instance.enableAccountStorageEvents();

      this.msalBroadcastService.msalSubject$
        .pipe(
          filter(
            (msg: EventMessage) =>
              msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED
          )
        )
        .subscribe((result: any) => {
          if (this.msalService.instance.getAllAccounts().length === 0) {
            this.graphService.ssoAuthenticated$.next(SSOLoginStatus.Logout);
          } else {
            this.setLoginDisplay();
            if (result?.payload?.account) {
              this.msalService.instance.setActiveAccount(result.payload.account);
              MsalConfig.account = result.payload.account;
              this.graphService.ssoAuthenticated$.next(SSOLoginStatus.Login);
            }
          }
        });

      this.msalBroadcastService.inProgress$
        .pipe(
          filter((status: InteractionStatus) => status === InteractionStatus.None),
          takeUntil(this._destroying$)
        )
        .subscribe(() => {
          this.setLoginDisplay();
          this.checkAndSetActiveAccount();
        });
    }
  }
  loginDisplay = false;
  private readonly _destroying$ = new Subject<void>();

  setLoginDisplay(): void {
    this.loginDisplay = this.msalService.instance.getAllAccounts().length > 0;
  }
  checkAndSetActiveAccount(): void {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    const activeAccount = this.msalService.instance.getActiveAccount();

    if (!activeAccount && this.msalService.instance.getAllAccounts().length > 0) {
      const accounts = this.msalService.instance.getAllAccounts();
      this.msalService.instance.setActiveAccount(accounts[0]);
      MsalConfig.account = accounts[0] as any;
      this.graphService.ssoAuthenticated$.next(SSOLoginStatus.Login);
    }
  }
  ngAfterViewInit(): void {
    this.subscription.add(
      this.localStorageService.getStorageItem(Identifier.TYPE_IS_SSO_LOGIN).subscribe((sso_login) => {
        if (sso_login === 'true') {
          setTimeout(() => {
            this.graphService.acquireToken();
            //this.loginPopup();
          }, 1000);
        }
      })
    );
  }

  getClinicApplicationSetup(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.subscription.add(
        forkJoin([
          this.clinicHttpService.getClinicApplicationSetup(this.currClinicId),
          //this.helpService.getAutoPopoverHelpIds()
        ]).subscribe(([applicationSetup /*, helpContentIds*/]) => {
          this.applicationSetup = applicationSetup;
          //this.localStorageService.setStorageItem(Identifier.TYPE_HELP_CONTENT_IDS, JSON.stringify(helpContentIds.helpContentIds ?? []));
          this.ref.markForCheck();
          resolve();
        })
      );
    });
  }

  private subscribeToTokenLocalStorage(): void {
    this.subscription.add(
      combineLatest([
        this.localStorageService.getStorageItem(Identifier.TYPE_LOGIN_STAFF),
        this.store.select(selectAuthenticatedUser),
      ]).subscribe(async ([staffInfo, isAuthenticated]) => {
        // getClinicApplicationSetup() must stay behind BOTH halves of this guard.
        //
        // TYPE_LOGIN_STAFF is a BehaviorSubject, so it emits its seed `null` during ngOnInit —
        // before anyone has logged in. But its *presence* is not a session either: the key is
        // written by UserEffects.saveUser and only removed on `unauthenticateUser`, so it
        // outlives an expiry the app never noticed, and it is already on disk at bootstrap
        // before the store has been hydrated. Gating on the key alone sent this request out
        // with an empty Authorization header; ErrorInterceptor turned that 401 into the
        // "username or password is incorrect" popup on a portal with no password field.
        //
        // selectAuthenticatedUser is the same check AuthInterceptorService uses to decide
        // whether to attach a Bearer token, so it answers the only question that matters here:
        // can this request be authenticated at all?
        if (!staffInfo || !isAuthenticated) {
          this.flushIdleTimer();
          return;
        }

        await this.getClinicApplicationSetup();

        const entity = JSON.parse(staffInfo);
        this.setIdleCheck(this.applicationSetup?.staffSessionInactivityTimeoutInMinutes || 1440, entity.token);
      })
    );
  }

  private setIdleCheck(inactivityInMinutes: number, token: string): void {
    this.flushIdleTimer();
    this.userIdle.setConfigValues({
      idle: inactivityInMinutes * 60,
      timeout: this.idleTimeOutLimit,
      ping: 120,
    });
    if (token && !this.jwtHelper.isTokenExpired(token)) {
      this.startWatching();
      this.idleSubscription.add(
        this.store
          .pipe(
            select(getUserIdle),
            filter((idle: boolean | undefined) => !!idle)
          )
          .subscribe(() => this.showConfirmDialog())
      );
      this.subscribeToTimerStart();
      this.subscribeToTimerTimeOut();
    } else {
      this.store.dispatch(setUserIdle({ isIdle: false }));
    }
  }

  private subscribeToTimerStart(): void {
    this.idleSubscription.add(
      this.userIdle.onTimerStart().subscribe((count) => {
        if (count === 1) {
          this.store.dispatch(setUserIdle({ isIdle: true }));
        }
        this.userSecondsIdle = count;
        this.ref.markForCheck();
      })
    );
  }

  private subscribeToTimerTimeOut(): void {
    this.idleSubscription.add(
      this.userIdle.onTimeout().subscribe(() => {
        if (this.displayIdleConfirmDialog) {
          this.displayIdleConfirmDialog = false;
        }
        this.endIdleTimerAndSession();
      })
    );
  }

  private showConfirmDialog(): void {
    this.displayIdleConfirmDialog = true;
    this.ref.markForCheck();
  }

  private startWatching(): void {
    this.userIdle.startWatching();
  }

  private flushIdleTimer(): void {
    this.userIdle.stopWatching();
    this.displayIdleConfirmDialog = false;
    this.idleSubscription.unsubscribe();
    this.idleSubscription = new Subscription();
  }

  ngOnDestroy() {
    if (this.loadSub) this.loadSub.unsubscribe();
    this.loadSub = null;

    this.subscription.unsubscribe();
    this.timerSub?.unsubscribe();
  }

  endIdleTimerAndSession(): void {
    this.flushIdleTimer();
    this.store.dispatch(setUserIdle({ isIdle: false }));
    if (this.authService.isSSOLogin) {
      // Broadcasts to the other tabs, as this path always has: an SSO sign-out ends the shared
      // Azure AD session, so leaving the other tabs "signed in" would only defer their 401.
      this.ssoLogoutService.logout();
      return;
    }
    this.logout(true);
  }

  restartIdleTimer(): void {
    this.userIdle.stopWatching();
    this.displayIdleConfirmDialog = false;
    this.userIdle.startWatching();
    this.store.dispatch(setUserIdle({ isIdle: false }));
  }

  logout(preventBroadCastLogout = false) {
    this.authService.logout(preventBroadCastLogout);
  }

  private startProgress() {
    this.progressValue = 0;
    this.showProgress = true;

    this.timerSub?.unsubscribe();

    this.timerSub = interval(200).subscribe(() => {
      if (this.progressValue < 95) {
        this.progressValue += 5;
        this.ref.detectChanges();
      }
    });
  }

  private finishProgress() {
    this.progressValue = 100;
    this.ref.detectChanges();

    this.timerSub?.unsubscribe();

    setTimeout(() => {
      this.showProgress = false;
      this.ref.detectChanges();
    }, 500);
  }
}
